/**
 * 🖤 LES 7 PÉCHÉS CAPITAUX - Le Quiz de l'Ombre
 * 
 * "Tout le monde a un péché dominant..."
 * 
 * 7 Péchés :
 * 🔥 LUXURIA (Luxure) - La passion brûlante
 * 👑 SUPERBIA (Orgueil) - L'ego assumé  
 * 💚 INVIDIA (Envie) - Le désir de l'autre
 * 🍷 GULA (Gourmandise) - L'appétit insatiable
 * 💎 AVARITIA (Avarice) - La collection des conquêtes
 * ⚡ IRA (Colère) - La passion explosive
 * 🌙 ACEDIA (Paresse) - L'attraction magnétique
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Flame, Crown, Heart, Wine, Gem, Zap, Moon,
  ChevronRight, ChevronLeft, Check, Sparkles, Eye
} from 'lucide-react';
import { Button } from '../../ui/button';

// 🎭 LES 7 PÉCHÉS CAPITAUX
export const SEVEN_SINS = {
  luxuria: {
    id: 'luxuria',
    name: 'Luxure',
    latin: 'Luxuria',
    emoji: '🔥',
    icon: Flame,
    color: 'from-red-500 to-orange-600',
    darkColor: 'from-red-900 to-red-950',
    description: 'La passion brûlante, le désir assumé',
    shortDesc: 'Tu brûles de désir',
    traits: ['Passionné(e)', 'Sensuel(le)', 'Magnétique', 'Intense'],
    seekingTraits: ['Intensité', 'Passion', 'Connexion physique', 'Heartbeats'],
    compatibleWith: ['luxuria', 'ira', 'gula'],
    quote: '"Le désir est le feu qui consume l\'âme..."'
  },
  superbia: {
    id: 'superbia',
    name: 'Orgueil',
    latin: 'Superbia',
    emoji: '👑',
    icon: Crown,
    color: 'from-yellow-500 to-amber-600',
    darkColor: 'from-yellow-900 to-amber-950',
    description: 'L\'ego assumé, le besoin d\'admiration',
    shortDesc: 'Tu veux qu\'on t\'admire',
    traits: ['Confiant(e)', 'Charismatique', 'Ambitieux(se)', 'Dominant(e)'],
    seekingTraits: ['Admiration', 'Respect', 'Reconnaissance', 'Défi'],
    compatibleWith: ['superbia', 'invidia', 'avaritia'],
    quote: '"Je suis ma propre légende..."'
  },
  invidia: {
    id: 'invidia',
    name: 'Envie',
    latin: 'Invidia',
    emoji: '💚',
    icon: Eye,
    color: 'from-green-500 to-emerald-600',
    darkColor: 'from-green-900 to-emerald-950',
    description: 'Le désir de ce que les autres ont',
    shortDesc: 'Tu veux ce qu\'ils ont',
    traits: ['Observateur(rice)', 'Ambitieux(se)', 'Compétitif(ve)', 'Déterminé(e)'],
    seekingTraits: ['Challenge', 'Conquête', 'Trophée', 'Victoire'],
    compatibleWith: ['invidia', 'superbia', 'avaritia'],
    quote: '"Ce que tu as... je le veux."'
  },
  gula: {
    id: 'gula',
    name: 'Gourmandise',
    latin: 'Gula',
    emoji: '🍷',
    icon: Wine,
    color: 'from-purple-500 to-fuchsia-600',
    darkColor: 'from-purple-900 to-fuchsia-950',
    description: 'L\'appétit insatiable, tout goûter',
    shortDesc: 'Tu veux tout goûter',
    traits: ['Épicurien(ne)', 'Curieux(se)', 'Sensuel(le)', 'Aventurier(ère)'],
    seekingTraits: ['Nouvelles expériences', 'Plaisirs', 'Découvertes', 'Saveurs'],
    compatibleWith: ['gula', 'luxuria', 'acedia'],
    quote: '"La vie est un buffet... je goûte à tout."'
  },
  avaritia: {
    id: 'avaritia',
    name: 'Avarice',
    latin: 'Avaritia',
    emoji: '💎',
    icon: Gem,
    color: 'from-cyan-500 to-blue-600',
    darkColor: 'from-cyan-900 to-blue-950',
    description: 'Collectionner, posséder, garder',
    shortDesc: 'Tu collectionnes les conquêtes',
    traits: ['Sélectif(ve)', 'Exclusif(ve)', 'Possessif(ve)', 'Raffiné(e)'],
    seekingTraits: ['Rareté', 'Exclusivité', 'Valeur', 'Collection'],
    compatibleWith: ['avaritia', 'superbia', 'invidia'],
    quote: '"Tu seras mon plus beau trophée..."'
  },
  ira: {
    id: 'ira',
    name: 'Colère',
    latin: 'Ira',
    emoji: '⚡',
    icon: Zap,
    color: 'from-orange-500 to-red-600',
    darkColor: 'from-orange-900 to-red-950',
    description: 'La passion explosive, l\'intensité',
    shortDesc: 'Tu aimes les étincelles',
    traits: ['Passionné(e)', 'Explosif(ve)', 'Direct(e)', 'Intense'],
    seekingTraits: ['Confrontation', 'Passion', 'Débat', 'Étincelles'],
    compatibleWith: ['ira', 'luxuria', 'superbia'],
    quote: '"Les meilleures réconciliations suivent les meilleures disputes..."'
  },
  acedia: {
    id: 'acedia',
    name: 'Paresse',
    latin: 'Acedia',
    emoji: '🌙',
    icon: Moon,
    color: 'from-indigo-500 to-purple-600',
    darkColor: 'from-indigo-900 to-purple-950',
    description: 'L\'attraction magnétique, venir à soi',
    shortDesc: 'Tu attires à toi',
    traits: ['Mystérieux(se)', 'Magnétique', 'Détendu(e)', 'Irrésistible'],
    seekingTraits: ['Attraction', 'Mystère', 'Confort', 'Facilité'],
    compatibleWith: ['acedia', 'gula', 'luxuria'],
    quote: '"Pourquoi chasser quand on peut attirer ?"'
  }
};

// 📝 QUESTIONS DU QUIZ
const SHADOW_QUIZ_QUESTIONS = [
  {
    id: 'q1',
    question: '🌙 Dans une soirée, tu es plutôt...',
    answers: [
      { text: 'Celui/celle qu\'on remarque en premier', sin: 'superbia', emoji: '👑' },
      { text: 'À la chasse, je repère ma proie', sin: 'luxuria', emoji: '🔥' },
      { text: 'Je regarde qui est avec qui...', sin: 'invidia', emoji: '💚' },
      { text: 'Au bar, je goûte tous les cocktails', sin: 'gula', emoji: '🍷' },
      { text: 'Assis(e), j\'attends qu\'on vienne à moi', sin: 'acedia', emoji: '🌙' },
    ]
  },
  {
    id: 'q2', 
    question: '😈 Quand quelqu\'un te plaît vraiment...',
    answers: [
      { text: 'Je veux TOUT de cette personne', sin: 'luxuria', emoji: '🔥' },
      { text: 'Je veux qu\'elle m\'admire', sin: 'superbia', emoji: '👑' },
      { text: 'Je la veux parce que d\'autres la veulent', sin: 'invidia', emoji: '💚' },
      { text: 'Je la collectionne, c\'est un trophée', sin: 'avaritia', emoji: '💎' },
      { text: 'Qu\'elle vienne, je suis là', sin: 'acedia', emoji: '🌙' },
    ]
  },
  {
    id: 'q3',
    question: '🖤 En amour, tu préfères...',
    answers: [
      { text: 'La passion brûlante, quitte à se consumer', sin: 'luxuria', emoji: '🔥' },
      { text: 'Les disputes suivies de réconciliations intenses', sin: 'ira', emoji: '⚡' },
      { text: 'Quelqu\'un qui me met sur un piédestal', sin: 'superbia', emoji: '👑' },
      { text: 'Multiplier les expériences', sin: 'gula', emoji: '🍷' },
      { text: 'Une relation exclusive et rare', sin: 'avaritia', emoji: '💎' },
    ]
  },
  {
    id: 'q4',
    question: '🔥 Ta plus grande tentation...',
    answers: [
      { text: 'Le désir charnel, la sensualité', sin: 'luxuria', emoji: '🔥' },
      { text: 'Le pouvoir, être au-dessus', sin: 'superbia', emoji: '👑' },
      { text: 'Ce que les autres ont et pas moi', sin: 'invidia', emoji: '💚' },
      { text: 'Tout essayer, ne rien refuser', sin: 'gula', emoji: '🍷' },
      { text: 'Posséder, collectionner', sin: 'avaritia', emoji: '💎' },
    ]
  },
  {
    id: 'q5',
    question: '💀 Ton défaut assumé en séduction...',
    answers: [
      { text: 'Trop intense, je fais peur parfois', sin: 'luxuria', emoji: '🔥' },
      { text: 'Trop fier(ère), je ne fais pas le premier pas', sin: 'superbia', emoji: '👑' },
      { text: 'Trop jaloux(se), je compare tout', sin: 'invidia', emoji: '💚' },
      { text: 'Trop gourmand(e), jamais satisfait(e)', sin: 'gula', emoji: '🍷' },
      { text: 'Trop possessif(ve), je garde tout', sin: 'avaritia', emoji: '💎' },
      { text: 'Trop explosif(ve), je m\'emporte vite', sin: 'ira', emoji: '⚡' },
      { text: 'Trop passif(ve), j\'attends trop', sin: 'acedia', emoji: '🌙' },
    ]
  },
  {
    id: 'q6',
    question: '🌙 Ce qui t\'excite le plus...',
    answers: [
      { text: 'La tension sexuelle palpable', sin: 'luxuria', emoji: '🔥' },
      { text: 'Voir quelqu\'un tomber sous mon charme', sin: 'superbia', emoji: '👑' },
      { text: 'Conquérir quelqu\'un de "pris"', sin: 'invidia', emoji: '💚' },
      { text: 'Découvrir de nouvelles sensations', sin: 'gula', emoji: '🍷' },
      { text: 'Ajouter une perle rare à ma collection', sin: 'avaritia', emoji: '💎' },
      { text: 'Une dispute passionnée qui finit...', sin: 'ira', emoji: '⚡' },
      { text: 'Quand c\'est l\'autre qui fait tout le travail', sin: 'acedia', emoji: '🌙' },
    ]
  },
  {
    id: 'q7',
    question: '😏 Ta phrase secrète...',
    answers: [
      { text: '"Je te veux. Maintenant."', sin: 'luxuria', emoji: '🔥' },
      { text: '"Tu devrais t\'estimer heureux(se)..."', sin: 'superbia', emoji: '👑' },
      { text: '"Il/elle est avec qui déjà ?"', sin: 'invidia', emoji: '💚' },
      { text: '"Encore un(e)... et après ?"', sin: 'gula', emoji: '🍷' },
      { text: '"Tu es à moi."', sin: 'avaritia', emoji: '💎' },
      { text: '"Tu m\'énerves... j\'adore ça."', sin: 'ira', emoji: '⚡' },
      { text: '"Viens à moi..."', sin: 'acedia', emoji: '🌙' },
    ]
  }
];

// 🧮 Calculer le péché dominant
export const calculateDominantSin = (answers) => {
  const sinCounts = {};
  
  Object.values(answers).forEach(sin => {
    sinCounts[sin] = (sinCounts[sin] || 0) + 1;
  });
  
  // Trier par nombre
  const sorted = Object.entries(sinCounts).sort((a, b) => b[1] - a[1]);
  
  // Retourner le dominant + secondaire
  return {
    dominant: sorted[0]?.[0] || 'luxuria',
    secondary: sorted[1]?.[0] || null,
    scores: sinCounts
  };
};

// 🧮 Calculer la compatibilité des péchés
export const calculateShadowCompatibility = (profile1, profile2) => {
  const sin1 = profile1.dominantSin;
  const sin2 = profile2.dominantSin;
  
  if (!sin1 || !sin2) return { percentage: 50, level: 'unknown', chemistry: 'neutral' };
  
  const sinInfo1 = SEVEN_SINS[sin1];
  const sinInfo2 = SEVEN_SINS[sin2];
  
  let score = 30; // Base score
  let chemistry = 'neutral';
  let message = '';
  
  // Même péché = haute intensité
  if (sin1 === sin2) {
    score += 40;
    chemistry = 'mirror';
    message = `Deux ${sinInfo1.name}... L'intensité sera au maximum.`;
  }
  // Compatibles
  else if (sinInfo1.compatibleWith.includes(sin2)) {
    score += 30;
    chemistry = 'compatible';
    message = `${sinInfo1.name} & ${sinInfo2.name}... Un duo dangereux.`;
  }
  // Opposés = tension intéressante
  else {
    score += 15;
    chemistry = 'tension';
    message = `${sinInfo1.name} vs ${sinInfo2.name}... Ça va être électrique.`;
  }
  
  // Bonus péchés secondaires compatibles
  if (profile1.secondarySin && profile2.secondarySin) {
    if (profile1.secondarySin === profile2.secondarySin) {
      score += 15;
    }
  }
  
  // Niveau de combustion
  let level;
  if (score >= 75) level = 'infernal';
  else if (score >= 60) level = 'burning';
  else if (score >= 45) level = 'heated';
  else level = 'smoldering';
  
  return {
    percentage: Math.min(score, 100),
    level,
    chemistry,
    message,
    sin1: sinInfo1,
    sin2: sinInfo2
  };
};

// 🎨 COMPOSANT QUIZ DES PÉCHÉS
export const ShadowQuiz = ({ onComplete, onCancel }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  
  const question = SHADOW_QUIZ_QUESTIONS[currentQuestion];
  const progress = ((currentQuestion + 1) / SHADOW_QUIZ_QUESTIONS.length) * 100;
  
  const handleAnswer = (sin) => {
    const newAnswers = { ...answers, [question.id]: sin };
    setAnswers(newAnswers);
    
    if (currentQuestion < SHADOW_QUIZ_QUESTIONS.length - 1) {
      setTimeout(() => setCurrentQuestion(prev => prev + 1), 300);
    } else {
      // Quiz terminé
      const result = calculateDominantSin(newAnswers);
      setResult(result);
      setShowResult(true);
    }
  };
  
  const handleComplete = () => {
    onComplete({
      dominantSin: result.dominant,
      secondarySin: result.secondary,
      sinScores: result.scores,
      quizCompleted: true
    });
  };
  
  // Écran de résultat
  if (showResult && result) {
    const dominantSin = SEVEN_SINS[result.dominant];
    const secondarySin = result.secondary ? SEVEN_SINS[result.secondary] : null;
    
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-6 text-center"
      >
        {/* Reveal animation */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', bounce: 0.5 }}
          className="mb-6"
        >
          <div className={`w-24 h-24 mx-auto rounded-full bg-gradient-to-br ${dominantSin.darkColor} flex items-center justify-center text-5xl shadow-2xl`}>
            {dominantSin.emoji}
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h3 className="text-2xl font-black text-white mb-2">
            {dominantSin.name.toUpperCase()}
          </h3>
          <p className="text-gray-400 italic mb-4">{dominantSin.latin}</p>
          
          <p className="text-purple-300 mb-4">{dominantSin.description}</p>
          
          <div className="text-sm text-gray-500 mb-6 italic">
            "{dominantSin.quote}"
          </div>
          
          {/* Traits */}
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {dominantSin.traits.map((trait, idx) => (
              <span 
                key={idx}
                className={`px-3 py-1 rounded-full text-sm bg-gradient-to-r ${dominantSin.color} text-white`}
              >
                {trait}
              </span>
            ))}
          </div>
          
          {/* Péché secondaire */}
          {secondarySin && (
            <div className="mb-6 p-3 rounded-xl bg-gray-800/50">
              <p className="text-xs text-gray-500 mb-1">Péché secondaire</p>
              <div className="flex items-center justify-center gap-2">
                <span className="text-xl">{secondarySin.emoji}</span>
                <span className="text-gray-300">{secondarySin.name}</span>
              </div>
            </div>
          )}
          
          <Button
            onClick={handleComplete}
            className={`w-full bg-gradient-to-r ${dominantSin.color} text-white py-3`}
          >
            J'assume mon ombre 🖤
          </Button>
        </motion.div>
      </motion.div>
    );
  }
  
  // Quiz en cours
  return (
    <div className="p-6">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between text-xs text-gray-500 mb-2">
          <span>Question {currentQuestion + 1}/{SHADOW_QUIZ_QUESTIONS.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
          />
        </div>
      </div>
      
      {/* Question */}
      <AnimatePresence mode="wait">
        <motion.div
          key={question.id}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
        >
          <h3 className="text-xl font-bold text-white mb-6 text-center">
            {question.question}
          </h3>
          
          <div className="space-y-3">
            {question.answers.map((answer, idx) => (
              <motion.button
                key={idx}
                whileHover={{ scale: 1.02, x: 5 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleAnswer(answer.sin)}
                className={`w-full p-4 rounded-xl text-left transition-all
                  bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700 hover:border-purple-500/50
                  ${answers[question.id] === answer.sin ? 'border-purple-500 bg-purple-900/30' : ''}
                `}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{answer.emoji}</span>
                  <span className="text-gray-200">{answer.text}</span>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
      
      {/* Navigation */}
      {currentQuestion > 0 && (
        <Button
          variant="ghost"
          onClick={() => setCurrentQuestion(prev => prev - 1)}
          className="mt-4 text-gray-400"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Question précédente
        </Button>
      )}
    </div>
  );
};

// 🎨 AFFICHAGE DU PÉCHÉ (Badge)
export const SinBadge = ({ sin, size = 'md', showName = true }) => {
  const sinInfo = SEVEN_SINS[sin];
  if (!sinInfo) return null;
  
  const sizes = {
    sm: 'w-8 h-8 text-lg',
    md: 'w-12 h-12 text-2xl',
    lg: 'w-16 h-16 text-3xl',
    xl: 'w-20 h-20 text-4xl'
  };
  
  return (
    <div className="flex flex-col items-center gap-1">
      <div className={`${sizes[size]} rounded-full bg-gradient-to-br ${sinInfo.darkColor} flex items-center justify-center shadow-lg`}>
        {sinInfo.emoji}
      </div>
      {showName && (
        <span className="text-xs text-gray-400">{sinInfo.name}</span>
      )}
    </div>
  );
};

// 🔥 COMPATIBILITÉ DISPLAY
export const ShadowCompatibilityBadge = ({ compatibility, size = 'md' }) => {
  const levelColors = {
    infernal: { bg: 'from-red-600 to-orange-600', text: '🔥 INFERNAL', glow: 'shadow-red-500/50' },
    burning: { bg: 'from-orange-500 to-yellow-500', text: '⚡ BRÛLANT', glow: 'shadow-orange-500/50' },
    heated: { bg: 'from-purple-500 to-pink-500', text: '💜 CHAUD', glow: 'shadow-purple-500/50' },
    smoldering: { bg: 'from-gray-600 to-gray-700', text: '🌙 COUVANT', glow: 'shadow-gray-500/30' },
  };
  
  const level = levelColors[compatibility.level] || levelColors.smoldering;
  
  return (
    <div className="flex flex-col items-center">
      <div className={`px-4 py-2 rounded-full bg-gradient-to-r ${level.bg} text-white font-bold shadow-lg ${level.glow}`}>
        {compatibility.percentage}% {level.text}
      </div>
      {compatibility.message && (
        <p className="text-xs text-gray-400 mt-1 text-center max-w-[200px]">
          {compatibility.message}
        </p>
      )}
    </div>
  );
};

export default ShadowQuiz;
